import { useMemo, useState } from 'react';
import { api } from '../api';
import { useI18n } from '../i18n';
import { Field, Modal, Spinner } from '../components';

type Staff = Awaited<ReturnType<typeof api.staff>>;
type Member = Staff['staff'][number];

/**
 * Editing what one person can do.
 *
 * A role is a starting point, not a cage. Most clinics need "a receptionist,
 * but this one also handles the bank" or "a dentist who must not see other
 * dentists' earnings", and forcing that through invented role names ends with
 * a dozen near-identical roles nobody can tell apart.
 *
 * So each permission shows WHERE IT COMES FROM. Ticking one the role does not
 * include grants it to this person; unticking one the role does include revokes
 * it from them. The role itself is never edited, which is what makes the change
 * explainable later — and reversible with one button.
 *
 * Two rules are enforced on the server as well as shown here, because an
 * interface check is a courtesy and not a control:
 *
 *   YOU CANNOT GRANT WHAT YOU DO NOT HOLD. A manager without `finance.refund`
 *   cannot hand it to anybody. Those rows are visibly disabled and say why.
 *
 *   PLATFORM PERMISSIONS ARE NOT CLINIC PERMISSIONS. Nothing under `platform.`
 *   appears here at all; the server refuses them outright.
 */
export function PermissionEditor({
  member,
  roleTemplates,
  allPermissions,
  myPermissions,
  onClose,
  onSaved,
}: {
  member: Member;
  roleTemplates: Record<string, readonly string[]>;
  allPermissions: readonly string[];
  myPermissions: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, tx } = useI18n();
  const [roles, setRoles] = useState<string[]>(member.roles);
  const [granted, setGranted] = useState<string[]>(member.granted);
  const [revoked, setRevoked] = useState<string[]>(member.revoked);
  const [publicTitle, setPublicTitle] = useState(member.publicTitle ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mine = useMemo(() => new Set(myPermissions), [myPermissions]);

  /** What the chosen roles give, before this person's own overrides. */
  const fromRoles = useMemo(() => {
    const set = new Set<string>();
    for (const r of roles) for (const p of roleTemplates[r] ?? []) set.add(p);
    return set;
  }, [roles, roleTemplates]);

  /** Clinic permissions only, grouped by the area of the product they govern. */
  const groups = useMemo(() => {
    const byArea = new Map<string, string[]>();
    for (const p of allPermissions) {
      if (p.startsWith('platform.')) continue;
      const area = p.split('.')[0] ?? 'other';
      const list = byArea.get(area) ?? [];
      list.push(p);
      byArea.set(area, list);
    }
    return [...byArea.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [allPermissions]);

  const effective = useMemo(() => {
    const set = new Set(fromRoles);
    for (const p of granted) set.add(p);
    for (const p of revoked) set.delete(p);
    return set;
  }, [fromRoles, granted, revoked]);

  function toggle(permission: string) {
    const inRole = fromRoles.has(permission);
    const on = effective.has(permission);

    if (on) {
      // Turning it off: drop an explicit grant, or add an explicit revoke.
      setGranted((g) => g.filter((x) => x !== permission));
      if (inRole) setRevoked((r) => (r.includes(permission) ? r : [...r, permission]));
    } else {
      // Turning it on: drop the revoke, or add an explicit grant.
      setRevoked((r) => r.filter((x) => x !== permission));
      if (!inRole) setGranted((g) => (g.includes(permission) ? g : [...g, permission]));
    }
  }

  const dirty =
    publicTitle !== (member.publicTitle ?? '') ||
    roles.join() !== member.roles.join() ||
    [...granted].sort().join() !== [...member.granted].sort().join() ||
    [...revoked].sort().join() !== [...member.revoked].sort().join();

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.updateStaff(member.membershipId, {
        roles,
        granted,
        revoked,
        publicTitle: publicTitle.trim() || null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tx(t.settings.permissionsFor, { name: member.name })}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setGranted([]);
              setRevoked([]);
            }}
            disabled={busy || (granted.length === 0 && revoked.length === 0)}
          >
            {t.settings.resetToRole}
          </button>
          <button type="button" className="btn" onClick={() => void save()} disabled={busy || !dirty}>
            {busy ? <Spinner /> : t.settings.save}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 18 }}>
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <div className="notice">
          <span>{t.settings.permissionHelp}</span>
        </div>

        <div className="grid-2">
          <Field label={t.settings.roleTemplate} required>
            <select
              value={roles[0] ?? ''}
              onChange={(e) => setRoles(e.target.value ? [e.target.value] : [])}
            >
              <option value="">—</option>
              {Object.keys(roleTemplates)
                .filter((r) => r !== 'platform_owner')
                .map((r) => (
                  <option key={r} value={r}>
                    {r.replace(/_/g, ' ')}
                  </option>
                ))}
            </select>
          </Field>
          {member.isDentist ? (
            <Field label={t.dentists.publicTitle} hint={t.dentists.publicTitleHint}>
              <input value={publicTitle} onChange={(e) => setPublicTitle(e.target.value)} />
            </Field>
          ) : null}
        </div>

        <div className="row" style={{ flexWrap: 'wrap' }}>
          <span className="badge brand">
            {effective.size} {t.settings.effective}
          </span>
          {granted.length > 0 ? (
            <span className="badge ok">
              +{granted.length} {t.settings.granted}
            </span>
          ) : null}
          {revoked.length > 0 ? (
            <span className="badge danger">
              −{revoked.length} {t.settings.revoked}
            </span>
          ) : null}
        </div>

        <div className="perm-groups">
          {groups.map(([area, permissions]) => (
            <section key={area}>
              <div className="perm-group-head">
                <strong>
                  {(t.settings.permissionAreas as Record<string, string>)[area] ?? area.replace(/_/g, ' ')}
                </strong>
                <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                  {permissions.filter((p) => effective.has(p)).length} / {permissions.length}
                </span>
              </div>
              <div className="perm-grid">
                {permissions.map((p) => {
                  const on = effective.has(p);
                  const inRole = fromRoles.has(p);
                  const isGranted = granted.includes(p);
                  const isRevoked = revoked.includes(p);
                  // Delegation: you can never hand out what you do not hold.
                  const delegatable = mine.has(p);
                  const origin = isRevoked ? 'revoked' : isGranted ? 'granted' : inRole ? 'role' : null;

                  return (
                    <label
                      key={p}
                      className={`perm-chip${isGranted ? ' granted' : ''}${isRevoked ? ' revoked' : ''}${
                        !isGranted && !isRevoked && inRole ? ' from-role' : ''
                      }`}
                      title={delegatable ? undefined : t.settings.cannotDelegate}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={!delegatable && !on}
                        onChange={() => toggle(p)}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontWeight: 500 }}>{label(p)}</strong>
                        <code>{p}</code>
                      </span>
                      {origin ? (
                        <span className="perm-origin">
                          {(t.settings.permissionOrigin as Record<string, string>)[origin]}
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </Modal>
  );
}

/**
 * A readable name for a permission.
 *
 * Derived from the identifier rather than hand-translated: a new permission
 * added on the server appears here immediately and legibly, instead of
 * appearing as a blank until somebody remembers to add a string for it.
 */
function label(permission: string): string {
  const [, ...rest] = permission.split('.');
  const words = rest.join(' ').replace(/[._]/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
